import React from "react";
import { Stack, Typography, Box } from "@mui/material";
import { tokens } from "@/tokens";

const STATUS_COLORS: Record<string, string> = {
  active: "#059669",
  approved: "#059669",
  pass: "#059669",
  match: "#059669",
  verified: "#059669",
  cleared: "#059669",
  complete: "#059669",
  ACTIVE: "#059669",
  APPROVED: "#059669",
  PASS: "#059669",
  VERIFIED: "#059669",
  VALIDATED: "#059669",
  in_progress: "#2563EB",
  in_transit: "#2563EB",
  VALIDATION_IN_PROGRESS: "#2563EB",
  READY_FOR_VALIDATION: "#2563EB",
  pending: "#D97706",
  under_review: "#7C3AED",
  warning: "#D97706",
  needs_review: "#D97706",
  PENDING_REVIEW: "#D97706",
  WARNING: "#D97706",
  DOCUMENTS_PENDING: "#D97706",
  PENDING_UPLOAD: "#D97706",
  draft: "#6B7280",
  inactive: "#6B7280",
  DRAFT: "#6B7280",
  INACTIVE: "#6B7280",
  NOT_RUN: "#6B7280",
  rejected: "#DC2626",
  fail: "#DC2626",
  mismatch: "#DC2626",
  missing: "#DC2626",
  REJECTED: "#DC2626",
  FAIL: "#DC2626",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  approved: "Approved",
  pass: "Pass",
  match: "Match",
  verified: "Verified",
  ACTIVE: "Active",
  APPROVED: "Approved",
  PASS: "Pass",
  VERIFIED: "Verified",
  VALIDATED: "Validated",
  in_progress: "In Progress",
  VALIDATION_IN_PROGRESS: "Validating",
  READY_FOR_VALIDATION: "Ready",
  pending: "Pending",
  under_review: "Under Review",
  warning: "Warning",
  needs_review: "Needs Review",
  PENDING_REVIEW: "Pending Review",
  WARNING: "Warning",
  DOCUMENTS_PENDING: "Docs Pending",
  PENDING_UPLOAD: "Pending",
  draft: "Draft",
  inactive: "Inactive",
  DRAFT: "Draft",
  INACTIVE: "Inactive",
  NOT_RUN: "Not Run",
  rejected: "Rejected",
  fail: "Fail",
  mismatch: "Mismatch",
  missing: "Missing",
  REJECTED: "Rejected",
  FAIL: "Fail",
};

const CheckIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 5.5L4 7.5L8 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ClockIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <circle cx="5" cy="5" r="3.5" stroke={color} strokeWidth="1.2" />
    <path d="M5 3.5V5.5L6.5 6.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DotIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <circle cx="5" cy="5" r="2.5" fill={color} />
  </svg>
);

const getIcon = (status: string, color: string): React.ReactNode => {
  const s = status.toLowerCase();
  if (["active", "approved", "pass", "match", "verified", "cleared", "complete", "validated"].includes(s)) {
    return <CheckIcon color={color} />;
  }
  if (["rejected", "fail", "mismatch", "missing", "expired", "flagged"].includes(s)) {
    return <XIcon color={color} />;
  }
  if (["in_progress", "in_transit", "pending", "under_review", "needs_review", "incomplete", "pending_review", "documents_pending", "pending_upload", "validation_in_progress", "ready_for_validation", "warning"].includes(s)) {
    return <ClockIcon color={color} />;
  }
  return <DotIcon color={color} />;
};

interface StatusBadgeProps {
  status: string;
  label?: string;
  size?: "small" | "medium";
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label, size = "small" }) => {
  const color = STATUS_COLORS[status] || "#6B7280";
  const displayLabel = label || STATUS_LABELS[status] || status;
  const iconSize = size === "medium" ? 18 : 16;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box
        sx={{
          width: iconSize,
          height: iconSize,
          borderRadius: "50%",
          bgcolor: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {getIcon(status, color)}
      </Box>
      <Typography
        sx={{
          fontSize: size === "medium" ? tokens.fontSize.sm2 : tokens.fontSize.xs,
          fontWeight: tokens.fontWeight.semibold,
          color,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {displayLabel}
      </Typography>
    </Stack>
  );
};

export default StatusBadge;
